import React, { useState, useEffect } from 'react';
import { MDBRow, MDBCol } from 'mdbreact';
import PropTypes from 'prop-types';
import { orderBy } from 'lodash';
import { post } from 'axios';

import { actionSvgs } from 'assets/icons';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import FeaturedText from 'components/FeaturedText';
import Footer from 'components/Footer';
import Overlay from 'components/Informations/Overlay';
import ReturnPreviousPage from 'components/ReturnPreviousPage';
import Toast from 'components/Informations/Toast';

import DateField from 'components/FormGroup/DateField';
import ErrorMessage from 'components/FormGroup/ErrorMessage';
import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import { formatDate, isSameOrBefore } from 'utils/moment';
import { percentAllocation } from 'utils/masks';

import Accordion from 'components/Accordion';

import EditEmployeeToAllocate from '../../InternalComponents/EditEmployeeToAllocate';

import * as S from './styles';

if (!Array.isArray)
  Array.isArray = arg =>
    Object.prototype.toString.call(arg) === '[object Array]';

export default function AllocateProfessionals({ history, user, location }) {
  const { state } = location;

  const { id_funcionario: idFuncionario } = user;
  const { id_projeto: idProjeto } = state;

  const {
    getEmployeeResponsibility,
    getEmployeeFunction,
    getEmployeeLevel,
    getEmployeesToSelect,
    insertAlocation,
  } = endpoints;

  const [employeesList] = useFetch(getEmployeesToSelect);
  const [employeeLevelList] = useFetch(getEmployeeLevel);
  const [selectedFunctionList] = useFetch(getEmployeeFunction);
  const [employeeResponsabilityList] = useFetch(getEmployeeResponsibility);

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastType, setToastType] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastClose, setToastClose] = useState(2000);

  const [formAlterations, setFormAlterations] = useState(false);

  const [employeesToAllocateData, setEmployeesToAllocateData] = useState([]);
  const [employeesToAllocate, setEmployeesToAllocate] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeeFunction, setEmployeeFunction] = useState('');
  const [selectedFunction, setSelectedFunction] = useState({});
  const [employeeSeniorLevel, setEmployeeSeniorLevel] = useState('');
  const [selectedSeniorLevel, setSelectedSeniorLevel] = useState('');
  const [allocationInitialDate, setAllocationInitialDate] = useState(
    new Date(),
  );
  const [allocationFinalDate, setAllocationFinalDate] = useState(new Date());
  const [allocationPercent, setAllocationPercent] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successInsertion, setSuccessInsertion] = useState(false);

  const [currentEmployee, setCurrentEmployee] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [accordionOpen, setAccordionOpen] = useState(true);

  const [employeesOptions, setEmployeesOptions] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [functionsOptions, setFunctionsOptions] = useState([]);

  useEffect(() => {
    async function mountOptions(arr, fieldLabel, fieldValue) {
      const options = arr.map(option => ({
        label: option[fieldLabel],
        value: option[fieldValue],
      }));
      return options;
    }

    (async () => {
      const employeeOpts = await mountOptions(
        employeesList,
        'nome',
        'id_funcionario',
      );
      const employessLeveOpts = await mountOptions(
        employeeLevelList,
        'senioridade',
        'id_nivel',
      );
      const employeesFunctionOpts = await mountOptions(
        selectedFunctionList,
        'nome_funcao',
        'id_funcao',
      );
      setEmployeesOptions(orderBy(employeeOpts, 'label'));
      setLevelOptions(employessLeveOpts);
      setFunctionsOptions(employeesFunctionOpts);
    })();
    return () => {};
  }, [
    employeesList,
    employeeLevelList,
    selectedFunctionList,
    employeeResponsabilityList,
  ]);

  useEffect(() => {
    setFormAlterations(true);
    setSelectedEmployee('');
    setSelectedFunction(0);
    setSelectedSeniorLevel(0);
    setAllocationInitialDate(new Date());
    setAllocationFinalDate(new Date());
    setEmployeeName('');
    setEmployeeFunction('');
    setEmployeeSeniorLevel('');
    setAllocationPercent('');
  }, [employeesToAllocateData]);

  const includeEmployee = (
    name,
    area,
    seniorLevel,
    initialDate,
    finalDate,
    percent,
    idFunc,
    idFuncao,
    idNivel,
  ) => {
    if (
      !name ||
      !area ||
      !seniorLevel ||
      !initialDate ||
      !finalDate ||
      !percent
    ) {
      return renderToast(
        'error',
        'Preencha todos os campos adequadamente',
        6000,
      );
    }

    const employeeInitialAllocation = formatDate(initialDate);
    const employeeFinalAllocation = formatDate(finalDate);

    setEmployeesToAllocate(prev => [
      {
        employeeInitialAllocation,
        employeeFinalAllocation,
        name,
        area,
        seniorLevel,
        percent: Number(percent),
        id: idFunc || selectedEmployee,
        idNivel: idNivel || selectedSeniorLevel,
        idFuncao: idFuncao || selectedFunction,
      },
      ...prev,
    ]);

    setEmployeesToAllocateData(prev => [
      {
        dt_inicio_alocacao: formatDate(initialDate, 'YYYY-MM-DD'),
        dt_final_alocacao: formatDate(finalDate, 'YYYY-MM-DD'),
        id_funcionario: idFunc || selectedEmployee,
        id_funcao: idFuncao || selectedFunction,
        id_nivel: idNivel || selectedSeniorLevel,
        percentual: parseInt(percent),
      },
      ...prev,
    ]);

    setEmployeesOptions(prev =>
      prev.filter(option => option.value !== selectedEmployee),
    );
  };

  const removeEmployee = (employeeId, employeename) => {
    const objOption = { label: employeename, value: employeeId };

    setEmployeesToAllocate(prev =>
      prev.filter(employee => employee.id !== employeeId),
    );

    setEmployeesToAllocateData(prev =>
      prev.filter(employee => employee.id_funcionario !== employeeId),
    );

    setEmployeesOptions(prev => orderBy([...prev, objOption], 'label'));
  };

  const renderToast = (type, message, closeTime) => {
    const condition = closeTime !== undefined ? closeTime : toastClose;

    setToastVisibility(true);
    setToastMessage(message);
    setToastType(type);
    setToastClose(condition);

    setTimeout(() => {
      setToastVisibility(false);
      setToastMessage('');
      setToastType('');
    }, condition + 1500);
  };

  const renderOverlay = type => {
    switch (type) {
      case 'submit':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="success"
            text={[
              'Você está ',
              <span>salvando</span>,
              ' todas as informações e ',
              <span>alocando todos os funcionários</span>,
              ' neste projeto. Tem certeza de que está tudo certo?',
            ]}
          >
            <UnfilledButton
              text="Não"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(false);
              }}
            />
            <FilledButton
              text="Sim, salvar"
              buttonClass="confirm"
              handleClick={() => {
                setIsSubmitting(true);
                const sendData = employeesToAllocateData;

                const send = {
                  id_usr_in: idFuncionario,
                  id_usr_up: idFuncionario,
                  id_projeto: idProjeto,
                  alocacoes: sendData,
                };

                const singular = {
                  success: 'Profissional alocado com sucesso',
                  warn:
                    'O profissional que está sendo alocado já faz parte deste projeto',
                  error:
                    'Erro ao inserir o profissional. Por favor tente novamente',
                };

                const plural = {
                  success: 'Profissionais alocados com sucesso.',
                  warn:
                    'Alguns dos profissionais que estão sendo alocados já fazem parte deste projeto',
                  error:
                    'Erro ao inserir os profissionais. Por favor tente novamente',
                };

                const success = response => {
                  const { status: responseStatus } = response;
                  switch (responseStatus) {
                    case 200:
                      setOverlayVisibility(false);
                      setOverlayType('');
                      setEmployeesToAllocate([]);
                      setEmployeesToAllocateData([]);
                      setIsSubmitting(false);
                      break;
                    default:
                      renderToast(
                        'error',
                        sendData.length > 1 ? plural.error : singular.error,
                        6000,
                      );
                      setOverlayVisibility(false);
                      setOverlayType('');
                      setIsSubmitting(false);
                      break;
                  }

                  return response;
                };

                const handleError = async err => {
                  const { response } = err;
                  const { data } = response;
                  const responseData = Array(data);
                  const isResponseAnArray = !!Array.isArray(responseData);

                  if (responseData && isResponseAnArray) {
                    setIsSubmitting(false);
                    setOverlayType('hasError');

                    return responseData.map(employee =>
                      setEmployeesToAllocate(currentArr =>
                        currentArr.map(current => {
                          if (current.id === employee.id_funcionario) {
                            return {
                              ...current,
                              alert: employee,
                            };
                          }
                          return current;
                        }),
                      ),
                    );
                  }
                  renderToast(
                    'error',
                    sendData.length > 1 ? plural.error : singular.error,
                    6000,
                  );
                  setOverlayVisibility(false);
                  setOverlayType('');
                };

                post(insertAlocation, send)
                  .then(success)
                  .then(() =>
                    localStorage.setItem(
                      'successToast',
                      JSON.stringify({
                        toastType: 'success',
                        toastMessage: `${
                          sendData.length > 1
                            ? plural.success
                            : singular.success
                        }`,
                        toastCloseTime: 8000,
                      }),
                    ),
                  )
                  .then(() => setSuccessInsertion(true))
                  .catch(handleError);
              }}
            />
          </Overlay>
        );

      case 'cancel':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cancelando</span>,
              ' a inserção ddos novos alocados neste projeto e esta ação irá ',
              <span>descartar</span>,
              ' suas alterações. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => {
                setOverlayVisibility(false);
                setOverlayType('');
                history.goBack();
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayVisibility(!overlayVisibility);
                setOverlayType('');
              }}
            />
          </Overlay>
        );

      case 'hasError':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="information"
            text={[
              'Existem funcionários ',
              <span>ultrapassando</span>,
              ' 100% de disponibilidade. ',
              <span>Revise</span>,
              ' os itens marcados em vermelho na lista',
            ]}
          >
            <FilledButton
              text="Ok"
              handleClick={() => {
                setOverlayVisibility(!overlayVisibility);
                setOverlayType('');
              }}
            />
          </Overlay>
        );

      case 'returning':
        return (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>voltando para a tela anterior</span>,
              ' e esta ação irá ',
              <span>descartar</span>,
              ' as alocações que não foram salvas. Deseja prosseguir com esta ação?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => history.goBack()}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => setOverlayVisibility(!overlayVisibility)}
            />
          </Overlay>
        );
      default:
        return '';
    }
  };

  const handleSetEmployee = e => {
    const selectedValue = e.value;
    const filterEmployee = employeesList.filter(
      option => option.id_funcionario === selectedValue,
    );
    const [obj] = filterEmployee;
    const {
      id_funcao: idFuncao,
      id_nivel: idNivel,
      nome_funcao: funcao,
      senioridade,
      nome,
    } = obj;

    setEmployeeName(nome);
    setEmployeeFunction(funcao);
    setEmployeeSeniorLevel(senioridade);
    setSelectedEmployee(selectedValue);
    setSelectedSeniorLevel(idNivel);
    setSelectedFunction(idFuncao);
    setFormAlterations(true);
  };

  return (
    <>
      {isModalOpen && (
        <EditEmployeeToAllocate
          includeEmployee={includeEmployee}
          allEmployees={employeesToAllocate}
          setAllEmployees={setEmployeesToAllocate}
          setEmployeesToAllocateData={setEmployeesToAllocateData}
          selectedEmployee={currentEmployee}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
        />
      )}

      {overlayVisibility && renderOverlay(overlayType)}

      {successInsertion && history.goBack()}

      {toastVisibility && (
        <Toast message={toastMessage} type={toastType} autoClose={toastClose} />
      )}

      <S.Container>
        <ReturnPreviousPage
          text="Voltar para gerência de projetos"
          handleClick={() => {
            const haveAlterations = () => {
              setOverlayVisibility(true);
              setOverlayType('returning');
            };

            const dontHaveAlterations = () => {
              setToastVisibility(false);
              history.goBack();
            };

            return formAlterations ? haveAlterations() : dontHaveAlterations();
          }}
        />
        <S.Introduction>
          <S.FeaturedText>{`${state.nome}`}</S.FeaturedText>
          <FeaturedText text="Aloque os funcionários que irão trabalhar neste projeto" />
        </S.Introduction>
        <MDBRow>
          <MDBCol lg="6" sm="12" />
        </MDBRow>
        <MDBRow>
          <MDBCol lg="6" sm="12">
            <S.Divider hasTopSpacing>Informações do funcionário</S.Divider>
            <S.Form>
              <SelectField
                options={employeesOptions}
                value={employeesOptions.find(
                  option => option.value === selectedEmployee,
                )}
                handleChange={handleSetEmployee}
                labelText="Nome do funcionário (*)"
                placeholderText="Escolha um funcionário..."
                name="selectedEmployee"
                hasLabelMargin
              />

              <SelectField
                options={functionsOptions}
                value={functionsOptions.find(
                  option => option.value === selectedFunction,
                )}
                handleChange={e => {
                  setSelectedFunction(e.value);
                  setEmployeeFunction(e.label);
                  setFormAlterations(true);
                }}
                labelText="Função do funcionário (*)"
                placeholderText="Selecione a função..."
                name="selectedFunction"
                hasLabelMargin
                isDisabled={employeeName.toLowerCase() !== 'sem recurso'}
              />

              <SelectField
                options={levelOptions}
                value={levelOptions.find(
                  option => option.value === selectedSeniorLevel,
                )}
                handleChange={e => {
                  setSelectedSeniorLevel(e.value);
                  setEmployeeSeniorLevel(e.label);
                  setFormAlterations(true);
                }}
                labelText="Nível(is) de senioridade (*)"
                placeholderText="Selecione o(s) nível(is)..."
                name="selectedSeniorLevel"
                isDisabled={employeeName.toLowerCase() !== 'sem recurso'}
                hasLabelMargin
              />

              <S.FlexRow>
                <div>
                  <DateField
                    handleChange={e => {
                      setAllocationInitialDate(e);
                      setFormAlterations(true);
                    }}
                    selectedValue={allocationInitialDate}
                    placeholderText="Selecione a data"
                    onlyMonth={false}
                    name="allocationInitialDate"
                    labelText="Data de início da alocação (*)"
                  />
                </div>

                <div>
                  <DateField
                    handleChange={e => {
                      setAllocationFinalDate(e);
                      setFormAlterations(true);
                    }}
                    selectedValue={new Date(allocationFinalDate)}
                    placeholderText="Selecione a data"
                    onlyMonth={false}
                    name="allocationFinalDate"
                    labelText="Data final da alocação (*)"
                  />
                </div>
              </S.FlexRow>

              <InputField
                labelText="Percentual de alocação (*)"
                placeholderText="Defina o percentual"
                value={allocationPercent}
                name="allocationPercent"
                width={50}
                handleChange={e => {
                  setAllocationPercent(percentAllocation(e.target.value));
                  setFormAlterations(true);
                }}
              />

              <S.Buttons>
                <FilledButton
                  text="+ Incluir funcionário"
                  disabled={!formAlterations}
                  handleClick={() => {
                    const condition =
                      employeeName &&
                      employeeFunction &&
                      employeeSeniorLevel &&
                      allocationInitialDate &&
                      allocationFinalDate &&
                      allocationPercent
                        ? isSameOrBefore(
                            allocationInitialDate,
                            allocationFinalDate,
                          )
                          ? includeEmployee(
                              employeeName,
                              employeeFunction,
                              employeeSeniorLevel,
                              allocationInitialDate,
                              allocationFinalDate,
                              allocationPercent,
                            )
                          : renderToast(
                              'error',
                              'A data final de alocação não pode ser menor do que a data inicial de alocação',
                              6000,
                            )
                        : renderToast(
                            'error',
                            'Preencha todos os campos adequadamente',
                            6000,
                          );

                    return condition;
                  }}
                />
              </S.Buttons>
            </S.Form>
          </MDBCol>
        </MDBRow>
        <MDBRow>
          <MDBCol lg="6" sm="12">
            <S.Divider>
              Abaixo estão todos os profissionais selecionados
            </S.Divider>
            <Accordion
              headText="Funcionários incluídos"
              callbackText="Você ainda não incluiu funcionários"
              accordionOpen={accordionOpen}
              setAccordionOpen={setAccordionOpen}
            >
              {employeesToAllocate.map(employee => (
                <>
                  <S.Employee
                    key={employee.name}
                    className={employee.alert ? 'hasError' : ''}
                  >
                    <div>
                      <p>{employee.name}</p>
                      <p>
                        {employee.area}
                        <small>({employee.seniorLevel})</small>
                      </p>
                      <div>
                        <p>
                          <span>Data de início:</span>{' '}
                          {employee.employeeInitialAllocation}
                        </p>
                        <p>
                          <span>Data final:</span>{' '}
                          {employee.employeeFinalAllocation}
                        </p>
                      </div>
                      <p>
                        <span>Percentual: </span> {employee.percent}%
                      </p>
                    </div>
                    <S.ActionButtons>
                      <button
                        type="button"
                        onClick={() => {
                          setCurrentEmployee(employee);
                          setIsModalOpen(true);
                        }}
                      >
                        {actionSvgs('edit', '#0083CA')}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          removeEmployee(employee.id, employee.name)
                        }
                      >
                        {actionSvgs('remove')}
                      </button>
                    </S.ActionButtons>
                  </S.Employee>
                  {employee.alert ? (
                    <S.Errors>
                      <ErrorMessage
                        message={`Disponibilidade de alocação: ${
                          100 - employee.alert.percentual
                        }%`}
                      />
                      <ErrorMessage
                        isBold={false}
                        message={`Possibilidade para alocação de 100% após ${formatDate(
                          employee.alert.dt_final_projeto_maior_deadline,
                          'DD/MM/YYYY',
                        )} (desalocação no ${
                          employee.alert.projeto_maior_deadline
                        })`}
                      />
                      <ErrorMessage
                        isBold={false}
                        message={`Projeto com maior percentual de alocação: ${employee.alert.projeto_max_percentual} (${employee.alert.percentual_max_projeto}%)`}
                      />
                    </S.Errors>
                  ) : (
                    ''
                  )}
                </>
              ))}
            </Accordion>
            <S.Buttons>
              <FilledButton
                text="Cancelar"
                buttonClass="cancel"
                handleClick={() => {
                  setOverlayVisibility(true);
                  setOverlayType('cancel');
                }}
              />
              <FilledButton
                text="Alocar todos"
                buttonClass="confirm"
                disabled={!employeesToAllocate.length || isSubmitting}
                handleClick={() => {
                  setOverlayVisibility(true);
                  setOverlayType('submit');
                }}
              />
            </S.Buttons>
          </MDBCol>
        </MDBRow>
      </S.Container>
      <Footer />
    </>
  );
}

AllocateProfessionals.defaultProps = {
  location: {},
  user: {},
};

AllocateProfessionals.propTypes = {
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ).isRequired,
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
};
